import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Box,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowIcon,
  Timeline as TimelineIcon,
  Loop as LoopIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

function EdgeDialog({
  open, onClose, edge, nodes, onSave,
}) {
  const [currentEdge, setCurrentEdge] = useState(edge || {
    id: `edge_${Date.now()}`,
    source: '',
    target: '',
    condition: '',
    label: '',
    type: 'edge-basic',
    description: '',
    priority: 1,
    timeout: null,
    retryCount: 0,
    metadata: {},
  });

  const [errors, setErrors] = useState({});
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (edge) {
      setCurrentEdge(edge);
    } else {
      setCurrentEdge({
        id: `edge_${Date.now()}`,
        source: '',
        target: '',
        condition: '',
        label: '',
        type: 'edge-basic',
        description: '',
        priority: 1,
        timeout: null,
        retryCount: 0,
        metadata: {},
      });
    }
    setErrors({});
    setValidationError('');
  }, [edge, open]);

  const edgeTypes = [
    {
      value: 'edge-basic', label: 'Basic', icon: <ArrowIcon />, color: '#1976d2', description: 'Simple connection between nodes',
    },
    {
      value: 'edge-conditional', label: 'Conditional', icon: <CodeIcon />, color: '#9c27b0', description: 'Connection based on condition',
    },
    {
      value: 'edge-success', label: 'Success', icon: <SuccessIcon />, color: '#4caf50', description: 'Connection on successful execution',
    },
    {
      value: 'edge-error', label: 'Error', icon: <ErrorIcon />, color: '#f44336', description: 'Connection on error or failure',
    },
    {
      value: 'edge-always', label: 'Always', icon: <TimelineIcon />, color: '#ff9800', description: 'Connection that always executes',
    },
    {
      value: 'edge-parallel', label: 'Parallel', icon: <PlayIcon />, color: '#2196f3', description: 'Parallel execution connection',
    },
    {
      value: 'edge-join', label: 'Join', icon: <TimelineIcon />, color: '#673ab7', description: 'Join multiple parallel branches',
    },
    {
      value: 'edge-loop', label: 'Loop', icon: <LoopIcon />, color: '#e91e63', description: 'Loop back to previous node',
    },
    {
      value: 'edge-timeout', label: 'Timeout', icon: <PauseIcon />, color: '#ff5722', description: 'Connection with timeout',
    },
    {
      value: 'edge-retry', label: 'Retry', icon: <StopIcon />, color: '#795548', description: 'Connection with retry logic',
    },
  ];

  const validateEdge = () => {
    const newErrors = {};

    if (!currentEdge.source) {
      newErrors.source = 'Source node is required';
    }

    if (!currentEdge.target) {
      newErrors.target = 'Target node is required';
    }

    if (currentEdge.source === currentEdge.target) {
      newErrors.target = 'Source and target cannot be the same';
    }

    // Check for circular connections
    if (currentEdge.source && currentEdge.target) {
      // This is a simplified check - in a real app you'd want more sophisticated cycle detection
      if (currentEdge.source === currentEdge.target) {
        newErrors.target = 'Cannot connect a node to itself';
      }
    }

    // Validate condition syntax for conditional edges
    if (currentEdge.type === 'edge-conditional' && currentEdge.condition) {
      try {
        // Basic JavaScript syntax validation
        new Function(currentEdge.condition);
      } catch (e) {
        newErrors.condition = 'Invalid JavaScript expression';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateEdge()) {
      onSave(currentEdge);
    }
  };

  const handleChange = (field, value) => {
    setCurrentEdge((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const getSourceNode = () => nodes.find((n) => n.id === currentEdge.source);
  const getTargetNode = () => nodes.find((n) => n.id === currentEdge.target);

  const getEdgeTypeInfo = (type) => edgeTypes.find((et) => et.value === type) || edgeTypes[0];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {edge?.source ? 'Edit Connection' : 'Add Connection'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Connection Type Selection */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Connection Type
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Edge Type</InputLabel>
                  <Select
                    value={currentEdge.type || 'edge-basic'}
                    onChange={(e) => handleChange('type', e.target.value)}
                    label="Edge Type"
                  >
                    {edgeTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ color: type.color }}>{type.icon}</Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {type.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {currentEdge.type && (
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      {getEdgeTypeInfo(currentEdge.type).description}
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Node Selection */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArrowIcon />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Node Connection
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl fullWidth error={!!errors.source}>
                  <InputLabel>Source Node</InputLabel>
                  <Select
                    value={currentEdge.source || ''}
                    onChange={(e) => handleChange('source', e.target.value)}
                    label="Source Node"
                  >
                    {nodes.map((node) => (
                      <MenuItem key={node.id} value={node.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={node.data?.type || 'node'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                          <Typography variant="body2">
                            {node.data?.label || node.id}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.source && <FormHelperText>{errors.source}</FormHelperText>}
                </FormControl>

                <FormControl fullWidth error={!!errors.target}>
                  <InputLabel>Target Node</InputLabel>
                  <Select
                    value={currentEdge.target || ''}
                    onChange={(e) => handleChange('target', e.target.value)}
                    label="Target Node"
                  >
                    {nodes.map((node) => (
                      <MenuItem key={node.id} value={node.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={node.data?.type || 'node'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                          <Typography variant="body2">
                            {node.data?.label || node.id}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.target && <FormHelperText>{errors.target}</FormHelperText>}
                </FormControl>

                {/* Connection Preview */}
                {currentEdge.source && currentEdge.target && (
                  <Box sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Connection Preview:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getSourceNode()?.data?.label || currentEdge.source}
                        size="small"
                        color="primary"
                      />
                      <ArrowIcon sx={{ color: 'text.secondary' }} />
                      <Chip
                        label={getTargetNode()?.data?.label || currentEdge.target}
                        size="small"
                        color="secondary"
                      />
                    </Box>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Edge Properties */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Edge Properties
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="Connection Label"
                  value={currentEdge.label || ''}
                  onChange={(e) => handleChange('label', e.target.value)}
                  placeholder="e.g., Success Path, Error Handler"
                  helperText="Optional label to identify this connection"
                />

                <TextField
                  label="Description"
                  value={currentEdge.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the purpose of this connection"
                  multiline
                  rows={2}
                  helperText="Optional description for documentation"
                />

                {/* Conditional Logic */}
                {(currentEdge.type === 'edge-conditional' || currentEdge.type === 'edge-success' || currentEdge.type === 'edge-error') && (
                  <TextField
                    label="Condition (JavaScript expression)"
                    value={currentEdge.condition || ''}
                    onChange={(e) => handleChange('condition', e.target.value)}
                    placeholder="e.g., output.success === true || result.status === 'completed'"
                    multiline
                    rows={3}
                    error={!!errors.condition}
                    helperText={
                      errors.condition
                      || "JavaScript expression that evaluates to true/false. Use 'output', 'result', or 'state' variables."
                    }
                  />
                )}

                {/* Advanced Properties */}
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Priority"
                    type="number"
                    value={currentEdge.priority || 1}
                    onChange={(e) => handleChange('priority', parseInt(e.target.value) || 1)}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ flex: 1 }}
                    helperText="Higher priority edges are evaluated first"
                  />

                  <TextField
                    label="Timeout (seconds)"
                    type="number"
                    value={currentEdge.timeout || ''}
                    onChange={(e) => handleChange('timeout', e.target.value ? parseInt(e.target.value) : null)}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                    helperText="Optional timeout for this connection"
                  />
                </Stack>

                {currentEdge.type === 'edge-retry' && (
                  <TextField
                    label="Retry Count"
                    type="number"
                    value={currentEdge.retryCount || 0}
                    onChange={(e) => handleChange('retryCount', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 10 }}
                    helperText="Number of times to retry this connection on failure"
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Validation Errors */}
          {Object.keys(errors).length > 0 && (
            <Alert severity="error">
              <Typography variant="body2">
                Please fix the following errors:
              </Typography>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {Object.values(errors).map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={Object.keys(errors).length > 0}
          startIcon={<SuccessIcon />}
        >
          Save Connection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EdgeDialog;
